import { MouseEvent, useContext, useState } from 'react';
import { CanvasContext } from 'context/canvas-context/canvas-context';
import { ActiveLayerContext } from 'context/active-layer-context/active-layer-context';
import { ToolbarContext } from 'context/toolbar-context/toolbar-context';
import { Selection } from 'canvas/selection';
import { TOOLS } from 'constants/tools';
import { TextEditorContext } from 'context/text-editor-context/text-editor-context';
import { getTransformedPoint } from 'utils/geometry';

export function useCanvasOnMove(isMousePressed: boolean) {
	const { setEditable } = useContext(TextEditorContext);
	const { renderer, renderingManager } = useContext(CanvasContext);
	const { activeLayer } = useContext(ActiveLayerContext);
	const { tool, setTool, setCursor, resizeDirection, setResizeDirection } =
		useContext(ToolbarContext);
	const [startPosition, setStartPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [timerId, setTimerId] = useState<number>(0);

	return function (event: MouseEvent) {
		if (renderer) {
			if (isMousePressed) {
				clearTimeout(timerId);
				setTimerId(0);
			}

			const currentTransformedPosition = getTransformedPoint(
				event.pageX,
				event.pageY,
				renderer.getTransform()
			);
			const pageX = currentTransformedPosition.x;
			const pageY = currentTransformedPosition.y;

			if (activeLayer && !isMousePressed) {
				const selection =
					activeLayer.getChildByType<Selection>('selection');
				if (selection) {
					const side = selection.isPointInStroke(pageX, pageY);
					const corner = selection.isPointInCorner(pageX, pageY);

					if (corner) {
						if (['top-left', 'bottom-right'].includes(corner)) {
							setCursor('nwse-resize');
						}
						if (['top-right', 'bottom-left'].includes(corner)) {
							setCursor('nesw-resize');
						}
						setResizeDirection(corner);
						setTool(TOOLS.RESIZER);
					} else if (side) {
						if (['top', 'bottom'].includes(side)) {
							setCursor('ns-resize');
						}
						if (['left', 'right'].includes(side)) {
							setCursor('ew-resize');
						}
						setResizeDirection(side);
						setTool(TOOLS.RESIZER);
					} else {
						setCursor('default');
						setTool(TOOLS.SELECT);
					}
				}
			}

			if (isMousePressed && tool === TOOLS.RESIZER) {
				if (activeLayer) {
					setEditable(false);
					activeLayer.resize(
						event.movementX,
						event.movementY,
						resizeDirection
					);
					renderingManager?.reDraw();
				}
			}

			if (isMousePressed && tool === TOOLS.SELECT) {
				if (activeLayer) {
					setEditable(false);
					activeLayer.move(event.movementX, event.movementY);
					renderingManager?.reDraw();
				}
			}

			if (isMousePressed && tool === TOOLS.HAND) {
				if (startPosition === null) {
					setStartPosition({ x: pageX, y: pageY });
				}

				if (startPosition !== null) {
					renderer
						.getContext()
						.translate(
							pageX - startPosition.x,
							pageY - startPosition.y
						);
					renderingManager?.reDraw();
				}
			}
			if (isMousePressed) {
				setTimerId(
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					setTimeout(() => {
						setStartPosition(null);
						document.dispatchEvent(
							new CustomEvent('moving-stopped')
						);
					}, 100)
				);
			}
		}
	};
}
